/*
====================================================================================
| MAIN.JS - THEME SCRIPT ORCHESTRATOR                                              |
|----------------------------------------------------------------------------------|
| This is the single entry point for all theme JavaScript.                         |
| 1. It imports the absolute core libraries needed everywhere.                     |
| 2. It defines page-specific "loaders" that run only if certain elements exist.   |
| 3. It uses dynamic import() to load modules on-demand, reducing initial load.    |
====================================================================================
*/

// Import core modules needed on every single page.
// The importmap allows us to use these clean names.
import '@theme/libs'; // Assuming this is for vendor libraries like Swiper, etc.
import '@theme/global'; // Assuming this initializes global things like the header, drawers, etc.
import utilities from '@theme/utilities'; // Example if utilities are needed here

/**
 * Page-Specific Loaders
 *
 * This object maps a CSS selector to an async function.
 * The function will dynamically import and initialize the modules
 * needed for components that match the selector.
 */
const pageLoaders = {
  // Loader for the main Product page
  '[data-section-type="product-main"]': async () => {
    console.log('Product section detected. Loading product-specific modules...');
    try {
      const { default: initProductZoom } = await import('@theme/zoom-psw');
      // You might also need the other Photoswipe module
      await import('@theme/psw');
      
      // Initialize the functionality from the imported modules
      initProductZoom();
      console.log('Product modules loaded and initialized.');
    } catch (e) {
      console.error('Failed to load product modules', e);
    }
  },

  // Loader for sections with floating elements (e.g., popups, tooltips)
  '.needs-floating-ui': async () => {
    console.log('Floating UI element detected. Loading floating module...');
    try {
      const { default: initFloatingElements } = await import('@theme/floating');
      initFloatingElements();
    } catch (e) {
      console.error('Failed to load floating module', e);
    }
  },

  // Example Loader for a Countdown Timer component
  '.product-countdown-timer': async () => {
    console.log('Countdown timer detected. Loading dayjs...');
    try {
      const { default: dayjs } = await import('@theme/dayjs');
      // Find all timers and initialize them with dayjs
      document.querySelectorAll('.product-countdown-timer').forEach(timer => {
        // ... your countdown initialization logic here using dayjs ...
      });
    } catch(e) {
      console.error('Failed to load dayjs for countdown', e);
    }
  }
};

/**
 * Execute Loaders
 *
 * Waits for the document to be ready, then iterates through the loaders
 * and runs the ones that match elements currently on the page.
 */
document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM ready. Checking for page-specific modules to load.');
  for (const selector in pageLoaders) {
    if (document.querySelector(selector)) {
      pageLoaders[selector]();
    }
  }
});